"use client";

import { Suspense, use, useEffect, useRef, useState } from "react";

import dynamic from "next/dynamic";
import Image from "next/image";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@repo/ui/components/ui/accordion";
import { useMessageBar } from "@repo/ui/contexts/MessageBarContext";
import { usePageContext } from "@repo/ui/contexts/PageContext";
import type {
  FlightWidgetCheckinTab,
  FlightWidgetFlightStatusTab,
  FlightWidgetTab,
  HomePageContent,
  SmartSearchFlightTab,
} from "@repo/ui/types";
import type { SiteSettings } from "@repo/ui/types/cms/config";
import type { GroupedStations } from "@repo/ui/types/stations";

import { recaptchaDetect } from "@/constants/recaptcha";
import type { NormalizedHomePageSections } from "@/features/home/helpers/normalizeHomePageSections";
import SearchSmart from "./SearchSmart";
import WidgetNavBar from "./WidgetNavBar";

const SearchWidgetsSkeleton = () => (
  <div className="w-full rounded-tr-lg rounded-b-lg bg-white p-4 sm:rounded-lg">
    <div className="animate-pulse">
      <div className="mb-4 grid grid-cols-5 gap-1">
        <div className="h-10 rounded-md bg-gray-200 sm:h-12"></div>
        <div className="h-10 rounded-md bg-gray-200 sm:h-12"></div>
        <div className="h-10 rounded-md bg-gray-200 sm:h-12"></div>
        <div className="h-10 rounded-md bg-gray-200 sm:h-12"></div>
        <div className="h-10 rounded-md bg-gray-200 sm:h-12"></div>
      </div>
      <div className="space-y-3">
        <div className="h-10 rounded-md bg-gray-200 sm:h-12"></div>
        <div className="h-10 rounded-md bg-gray-200 sm:h-12"></div>
        <div className="h-10 rounded-md bg-gray-200 sm:h-12"></div>
      </div>
    </div>
  </div>
);

type SearchSmartDataProps = Readonly<{
  destinationsPromise: Promise<GroupedStations | null>;
  siteSettings: SiteSettings;
  searchSmartTabs: SmartSearchFlightTab[];
}>;

const SearchSmartData = ({
  destinationsPromise,
  siteSettings,
  searchSmartTabs,
}: SearchSmartDataProps) => {
  const destinations = use(destinationsPromise);

  if (!destinations) {
    return <SearchWidgetsSkeleton />;
  }

  return (
    <SearchSmart
      destinations={destinations}
      siteSettings={siteSettings}
      searchSmartTabs={searchSmartTabs}
    />
  );
};
const ManageYourFlight = dynamic(() => import("./ManageYourFlight"), {
  loading: () => <div className="animate-pulse rounded bg-gray-200 p-4" />,
});
const CheckIn = dynamic(() => import("./CheckIn/CheckIn"), {
  loading: () => <div className="animate-pulse rounded bg-gray-200 p-4" />,
});
const FlightStatus = dynamic(() => import("./FlightStatus"), {
  loading: () => <div className="animate-pulse rounded bg-gray-200 p-4" />,
});

type HomePageWidgetProps = Readonly<{
  siteSettings: SiteSettings;
  destinationsPromise: Promise<GroupedStations | null>;
}>;

enum WidgetTabs {
  SearchSmart = "searchSmart",
  ManageYourFlight = "manageYourFlight",
  CheckIn = "checkIn",
  FlightStatus = "flightStatus",
}

export default function HomePageWidget({ siteSettings, destinationsPromise }: HomePageWidgetProps) {
  // ⚙️ CMS content
  const { normalizedSections } = usePageContext<HomePageContent, NormalizedHomePageSections>();

  const { widgets } = normalizedSections;

  const tabs = widgets?.tabs ?? [];

  const myReservation = tabs.find((tab) => tab.id === WidgetTabs.ManageYourFlight);
  const checkIn = tabs.find((tab) => tab.id === WidgetTabs.CheckIn);
  const flightStatus = tabs.find((tab) => tab.id === WidgetTabs.FlightStatus);

  const defaultActiveTab: FlightWidgetTab =
    tabs.find((tab) => tab.id === widgets.defaultTab) ?? tabs[0]!;

  const [activeTab, setActiveTab] = useState<FlightWidgetTab>(defaultActiveTab);

  // Get MessageBar visibility state from context
  const { isVisible: isMessageBarVisible } = useMessageBar();

  useEffect(() => {
    const clearBotFlags = () => {
      sessionStorage.removeItem(recaptchaDetect.mmbBot);
      sessionStorage.removeItem(recaptchaDetect.checkInBot);
    };
    // `pagehide` (unlike `beforeunload`) does not disable the back/forward cache.
    window.addEventListener("pagehide", clearBotFlags);
    return () => window.removeEventListener("pagehide", clearBotFlags);
  }, []);
  // Publishes the widget's live height via CSS var for the mobile carousel to position below it.
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;

    const publishHeight = () => {
      // offsetHeight excludes margin (mt-16 / mt-[120px]), so add it back in.
      const marginTop = Number.parseFloat(getComputedStyle(el).marginTop) || 0;
      document.documentElement.style.setProperty(
        "--home-widget-h",
        `${el.offsetHeight + marginTop}px`,
      );
    };

    publishHeight();
    const observer = new ResizeObserver(publishHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleTabChanged = (tab: FlightWidgetTab) => {
    setActiveTab(tab);
  };

  if (!activeTab) {
    return;
  }

  return (
    <main
      ref={mainRef}
      className={`responsive-layout relative z-10 w-full overflow-hidden bg-transparent p-4 md:overflow-visible md:py-0 ${
        isMessageBarVisible
          ? "mt-[120px] md:mt-0 md:mb-[-6.17rem] md:-translate-y-[calc(5.17rem)] lg:-translate-y-[calc(6.17rem)]"
          : "mt-16 md:mt-0 md:mb-[-6.17rem] md:-translate-y-[calc(8.67rem)] lg:-translate-y-[calc(9.67rem)]"
      }`}
      data-testid="homepage-widgets"
    >
      <WidgetNavBar widgets={widgets} activeTab={activeTab} onTabChanged={handleTabChanged} />

      {/* Desktop Views */}
      <div className="hidden sm:flex">
        {activeTab.id === WidgetTabs.SearchSmart && (
          <Suspense fallback={<SearchWidgetsSkeleton />}>
            <SearchSmartData
              destinationsPromise={destinationsPromise}
              siteSettings={siteSettings}
              searchSmartTabs={activeTab.content as SmartSearchFlightTab[]}
            />
          </Suspense>
        )}

        {activeTab.id === WidgetTabs.ManageYourFlight && (
          <ManageYourFlight content={activeTab.content?.[0] as FlightWidgetCheckinTab} />
        )}

        {activeTab.id === WidgetTabs.CheckIn && (
          <CheckIn content={activeTab.content?.[0] as FlightWidgetCheckinTab} />
        )}

        {activeTab.id === WidgetTabs.FlightStatus && (
          <FlightStatus content={activeTab.content?.[0] as FlightWidgetFlightStatusTab} />
        )}
      </div>

      {/* Mobile View - My Reservation as Accordion */}
      <div className="block pb-2 sm:hidden relative">
        {activeTab.id === WidgetTabs.SearchSmart && (
          <Suspense fallback={<SearchWidgetsSkeleton />}>
            <SearchSmartData
              destinationsPromise={destinationsPromise}
              siteSettings={siteSettings}
              searchSmartTabs={activeTab.content as SmartSearchFlightTab[]}
            />
          </Suspense>
        )}

        {activeTab.id === WidgetTabs.ManageYourFlight && (
          <Accordion
            type="single"
            collapsible
            className="relative z-20 w-full rounded-tl-lg rounded-b-lg bg-white p-3"
          >
            <AccordionItem value="manage">
              <AccordionTrigger>
                <span className="flex items-center gap-2">
                  <Image
                    src={myReservation?.activeIconUrl || ""}
                    alt="Administra tu vuelo"
                    width={20}
                    height={20}
                    className="h-5 w-5 object-contain"
                  />
                  {myReservation?.name || "Administra tu vuelo"}
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <ManageYourFlight
                  content={
                    (myReservation?.content?.[0] ||
                      activeTab.content?.[0]) as FlightWidgetCheckinTab
                  }
                />
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="checkin">
              <AccordionTrigger>
                <span className="flex items-center gap-2">
                  <Image
                    src={checkIn?.activeIconUrl || ""}
                    alt="Check-in"
                    width={20}
                    height={20}
                    className="h-5 w-5 object-contain"
                  />
                  {checkIn?.name || "Check-in"}
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <CheckIn
                  content={
                    (checkIn?.content?.[0] || activeTab.content?.[0]) as FlightWidgetCheckinTab
                  }
                />
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="status">
              <AccordionTrigger>
                <span className="flex items-center gap-2">
                  <Image
                    src={flightStatus?.activeIconUrl || ""}
                    alt="Estado del vuelo"
                    width={20}
                    height={20}
                    className="h-5 w-5 object-contain"
                  />
                  {flightStatus?.name || "Estado del vuelo"}
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <FlightStatus
                  content={
                    (flightStatus?.content?.[0] ||
                      activeTab.content?.[0]) as FlightWidgetFlightStatusTab
                  }
                />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
      </div>
    </main>
  );
}
